import React, { useEffect, useRef, useCallback } from 'react'

import useSound from '../../hooks/useSound'

import Toast from './Toast'

// Default duration (ms) for legacy string toasts (treated as "info")
const LEGACY_TOAST_DURATION = 3000

/**
 * Container for toasts with auto-dismiss functionality
 *
 * @component
 * @param {Array} props.toasts - toasts array (each with id, message, type, duration)
 * @param {Function} props.setToasts - toasts setter
 * @param {Function} props.removeToast - function to remove toast by ID
 * @returns {JSX.Element} The toasts container component.
 */
const ToastsContainer = ({ toasts, setToasts, removeToast }) => {
  const { playSound } = useSound()
  const previousToastsLength = useRef(0)
  const timersRef = useRef({})

  /**
   * Close toast by ID
   * @param {string} toastId - Unique toast identifier
   */
  const handleCloseToast = useCallback(
    (toastId) => {
      // Clear timer if exists
      if (timersRef.current[toastId]) {
        clearTimeout(timersRef.current[toastId])
        delete timersRef.current[toastId]
      }

      // Remove toast - handle legacy string toasts by index
      const legacyMatch = String(toastId).match(/^legacy-(\d+)$/)
      if (legacyMatch) {
        const idx = parseInt(legacyMatch[1], 10)
        setToasts((prev) => prev.filter((_, i) => i !== idx))
      } else if (removeToast) {
        removeToast(toastId)
      } else {
        setToasts((prev) => prev.filter((toast) => toast.id !== toastId))
      }
    },
    [removeToast, setToasts]
  )

  // Play sound when a new toast is added
  useEffect(() => {
    if (toasts.length > previousToastsLength.current) {
      // New toast added
      const newToast = toasts[toasts.length - 1]

      // Extract type from toast
      let type = 'info'
      if (typeof newToast === 'object' && newToast.type) {
        type = newToast.type
      }

      // Play appropriate sound
      playSound(type)
    }

    previousToastsLength.current = toasts.length
  }, [toasts, playSound])

  // Auto-dismiss toasts with configured durations
  useEffect(() => {
    toasts.forEach((toast) => {
      if (!toast || typeof toast === 'string') return
      // Only set timer if toast has duration and timer not already running
      if (toast.duration && !timersRef.current[toast.id]) {
        timersRef.current[toast.id] = setTimeout(() => {
          handleCloseToast(toast.id)
        }, toast.duration)
      }
    })

    // Cleanup timers for toasts that were removed
    const currentToastIds = new Set(
      toasts.filter((t) => t && typeof t !== 'string').map((t) => t.id)
    )
    Object.keys(timersRef.current).forEach((timerId) => {
      if (!currentToastIds.has(timerId)) {
        clearTimeout(timersRef.current[timerId])
        delete timersRef.current[timerId]
      }
    })

    // Cleanup all timers on unmount
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout)
      timersRef.current = {}
    }
  }, [toasts, handleCloseToast])

  return (
    <ul className="absolute flex flex-col-reverse gap-2 w-[348px] max-h-[500px] overflow-y-auto bottom-3 left-3 z-[10001]">
      {[...toasts].reverse().map((toast, revIndex) => {
        if (!toast) return null
        const originalIndex = toasts.length - 1 - revIndex
        // Normalize legacy string toasts to object format, with auto-dismiss
        const normalized =
          typeof toast === 'string'
            ? {
                id: `legacy-${originalIndex}`,
                message: toast,
                type: 'info',
                duration: LEGACY_TOAST_DURATION,
              }
            : toast
        return (
          <Toast
            key={normalized.id || `toast-${originalIndex}`}
            handleClose={() => handleCloseToast(normalized.id ?? originalIndex)}
            message={normalized}
          />
        )
      })}
    </ul>
  )
}

export default ToastsContainer
